import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ApiService } from '../shared/services/api.service';
import { Subject, takeUntil } from 'rxjs';
import { SearchData } from '@vg-constellation/angular-16/search';
import { LessonCardComponent } from '../shared/components/lesson-card/lesson-card.component';
import { LearningQuizFormValue } from './learning-content/learning-quiz/learning-quiz-form/learning-quiz-form.component';
import { TranslateService } from '@ngx-translate/core';
import {
  AdobeLaunchService,
  LearningLibrarySubPage,
} from '../shared/services/adobe-launch.service';
import {
  PAGE_TITLES,
  resolveSupportedLang,
} from '../shared/config/localized-paths';

interface Quiz {
  id: string;
  _path: string;
  learningTitle: string;
  learnDescription?: {
    html: string;
  };
  level?: string;
  imagePath?: {
    _path: string;
  } | null;
  jcr_uuid?: string;
}

interface LearningLibraryItem {
  learningLibraryId: string;
  title: string | null;
  score: number;
  quizStatus: string | null;
  lessonDueDate: string;
  level: string;
  totalQuestions: number | null;
  correctAnswered: number | null;
}

@Component({
  selector: 'app-learning-library',
  templateUrl: './learning-library.component.html',
  styleUrls: ['./learning-library.component.scss'],
  standalone: false,
})
export class LearningLibraryComponent {
  private readonly _destroyed$ = new Subject<void>();
  private intersectionObserver?: IntersectionObserver;
  private hasLoadedLessons = false;
  private _tryOpenInterval?: ReturnType<typeof setInterval>;
  private _tryOpenTimeout?: ReturnType<typeof setTimeout>;
  private _checkInterval?: ReturnType<typeof setInterval>;
  private _checkTimeout?: ReturnType<typeof setTimeout>;
  private _pendingFocusQuizId: string | undefined;

  @ViewChildren(LessonCardComponent)
  private lessonCards!: QueryList<LessonCardComponent>;

  @ViewChild('searchComponent', { read: ElementRef })
  searchComponentRef: ElementRef<HTMLElement> | undefined;
  searchData: SearchData = {
    columns: [],
    columnData: [],
  };

  difficultyLevels: number[] = [100, 200, 300];
  selectedDifficulties: string[] = [];
  searchTerm: string = '';
  searchResultText: string = '';
  private _announceTimer: ReturnType<typeof setTimeout> | null = null;
  selectedLessonId?: string;
  selectedLessonTitle?: string;
  selectedLessonPath?: string;
  showLessonContent: boolean = false;
  showQuizPage: boolean = false;
  showQuizQuestions: boolean = false;
  showQuizCompletion: boolean = false;
  quizScore: number = 0;
  totalQuizQuestions: number = 0;
  currentQuizData: any = null;
  currentQuizQuestions: any[] = [];
  quizFormValue?: LearningQuizFormValue;
  allLessons: Quiz[] = [];
  filteredLessons: Quiz[] = [];
  learningLibraries: LearningLibraryItem[] = [];
  dataLoaded: boolean = false;

  constructor(
    private _apiService: ApiService,
    private elementRef: ElementRef,
    private _cdr: ChangeDetectorRef,
    private _ngZone: NgZone,
    private readonly _adobeLaunchService: AdobeLaunchService,
    private readonly _translateService: TranslateService
  ) {}

  ngOnInit(): void {
    const lang = resolveSupportedLang(this._translateService.currentLang);
    this._adobeLaunchService.trackPageView({
      pageTitle: PAGE_TITLES[lang].learningLibrary,
    });

    this._apiService.userOpenLessonChanges$
      .pipe(takeUntil(this._destroyed$))
      .subscribe((request) => {
        this.triggerLessonOpen(
          request.lessonId,
          request.lessonPath,
          request.lessonTitle
        );
      });
  }

  private triggerLessonOpen(
    lessonId: string,
    lessonPath?: string,
    lessonTitle?: string
  ): void {
    if (!this.hasLoadedLessons) {
      this.fetchLessons();
      this.hasLoadedLessons = true;
    }
    this._ngZone.runOutsideAngular(() => {
      this._tryOpenInterval = setInterval(() => {
        if (this.dataLoaded && this.allLessons.length > 0) {
          clearInterval(this._tryOpenInterval);
          clearTimeout(this._tryOpenTimeout);
          const lesson = this.allLessons.find(
            (l) => l.id === lessonId || l.jcr_uuid === lessonId
          );
          if (lesson) {
            this._ngZone.run(() => {
              this.showQuizPage = false;
              this.showQuizQuestions = false;
              this.showQuizCompletion = false;
              this.showLessonContent = false;
              this.selectedLessonId = undefined;
              this._cdr.detectChanges();
              this.selectedLessonPath = lessonPath || lesson._path;
              this.selectedLessonTitle = lessonTitle || lesson.learningTitle;
              this.selectedLessonId = lessonId;
              this.showLessonContent = true;
              this._cdr.detectChanges();
              this.trackSubPage('lesson');
              setTimeout(
                () => window.scrollTo({ top: 0, behavior: 'smooth' }),
                100
              );
            });
          }
        }
      }, 100);
      this._tryOpenTimeout = setTimeout(
        () => clearInterval(this._tryOpenInterval),
        5000
      );
    });
  }

  ngAfterViewInit(): void {
    this.setupVisibilityObserver();
    this.lessonCards?.changes
      .pipe(takeUntil(this._destroyed$))
      .subscribe(() => {
        if (this._pendingFocusQuizId && this.lessonCards.length > 0) {
          const card = this.lessonCards.find(
            (c) => c.quizId === this._pendingFocusQuizId
          );
          card?.focusPrimaryButton();
          this._pendingFocusQuizId = undefined;
        }
      });
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();

    if (this._announceTimer !== null) {
      clearTimeout(this._announceTimer);
      this._announceTimer = null;
    }

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    clearInterval(this._tryOpenInterval);
    clearTimeout(this._tryOpenTimeout);
    clearInterval(this._checkInterval);
    clearTimeout(this._checkTimeout);
  }

  private setupVisibilityObserver(): void {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasLoadedLessons) {
            this.fetchLessons();
            this.hasLoadedLessons = true;

            if (this.intersectionObserver) {
              this.intersectionObserver.disconnect();
            }
          }
        });
      },
      {
        threshold: 0.1,
      }
    );

    this.intersectionObserver.observe(this.elementRef.nativeElement);
  }

  private fetchLessons(): void {
    this._apiService
      .getInvestLearningData()
      .pipe(takeUntil(this._destroyed$))
      .subscribe({
        next: (response) => {
          if (Array.isArray(response)) {
            this.allLessons = response;
          } else if (response && typeof response === 'object') {
            this.allLessons =
              response.data || response.items || response.lessons || [];
          } else {
            this.allLessons = [];
          }

          this.categorizeLessons();
          this.dataLoaded = true;
        },
        error: () => {
          this.dataLoaded = true;
        },
      });
  }

  private categorizeLessons(): void {
    this.filteredLessons = [];

    this.allLessons.forEach((lesson) => {
      const matchesSearch =
        !this.searchTerm ||
        lesson.learningTitle.toLowerCase().includes(this.searchTerm);

      const matchesDifficulty =
        this.selectedDifficulties.length === 0 ||
        this.selectedDifficulties.includes(lesson.level || '');

      if (!matchesSearch || !matchesDifficulty) {
        return;
      } else {
        this.filteredLessons.push(lesson);
      }
    });
  }

  getLearnDescription(quiz: Quiz): string {
    return quiz.learnDescription?.html || '';
  }

  getDifficultyLevel(level?: string): string | number {
    return level || '';
  }

  getImageUrl(quiz: Quiz): string | null {
    return quiz.imagePath?._path || null;
  }

  onSearchChange(event: any): void {
    const searchTerm = event?.searchTerm || '';
    this.searchTerm = searchTerm.toLowerCase();
    this.categorizeLessons();
    this.announceSearchResults();
  }

  private announceSearchResults(): void {
    if (this._announceTimer !== null) {
      clearTimeout(this._announceTimer);
      this._announceTimer = null;
    }

    if (!this.searchTerm) {
      this.searchResultText = '';
      return;
    }

    const total = this.filteredLessons.length;
    const message =
      total > 0
        ? `${total} search result${total === 1 ? '' : 's'} found`
        : 'No search results found';

    this.searchResultText = '';

    this._announceTimer = setTimeout(() => {
      this.searchResultText = message;
      this._announceTimer = null;
    }, 800);
  }

  onViewLesson(quiz: Quiz): void {
    this._pendingFocusQuizId = quiz.id;
    this.showLessonContent = false;
    this.selectedLessonPath = quiz._path;
    this.selectedLessonTitle = quiz.learningTitle;
    this.selectedLessonId = quiz.id;
    this.showLessonContent = true;
    this.trackSubPage('lesson');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onBackToLibrary(): void {
    this.selectedLessonId = undefined;
    this.selectedLessonTitle = undefined;
    this.showLessonContent = false;
    this.showQuizPage = false;
    this.showQuizQuestions = false;
    this.showQuizCompletion = false;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this._cdr.detectChanges();
  }

  onBackToLesson(): void {
    this.showQuizPage = false;
    this.showQuizQuestions = false;
    this.showQuizCompletion = false;
    this.trackSubPage('lesson');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onGoToQuiz(data: {
    lessonId: string;
    lessonTitle: string;
    quizData?: any;
  }): void {
    this.selectedLessonId = data.lessonId;
    this.selectedLessonTitle = data.lessonTitle;

    if (data.quizData) {
      this.currentQuizData = data.quizData;
      this.currentQuizQuestions = data.quizData.questionPath || [];
    }

    this.showQuizPage = true;
    this.showQuizQuestions = false;
    this.showQuizCompletion = false;
    this.trackSubPage('quiz_intro');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onStartQuiz(formValue?: LearningQuizFormValue): void {
    this.quizFormValue = formValue;
    this.showQuizQuestions = true;
    this.showQuizPage = false;
    this.showQuizCompletion = false;
    this.trackSubPage('quiz_questions');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onBackToQuizIntro(): void {
    this.showQuizQuestions = false;
    this.showQuizPage = true;
    this.showQuizCompletion = false;
    this.trackSubPage('quiz_intro');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  onSubmitQuiz(results: any): void {
    this.quizScore = results.correctAnswers || 0;
    this.totalQuizQuestions = results.totalQuestions || 0;
    this.trackSubPage('quiz_complete');

    const quizData = results.quizData;

    if (!quizData) {
      this.showQuizQuestions = false;
      this.showQuizPage = false;
      this.showQuizCompletion = true;
      return;
    }

    const quizId = this.selectedLessonId;

    const libraryItem = this.learningLibraries.find(
      (lib) => lib.learningLibraryId === quizId
    );

    const learningLibraryId = libraryItem?.learningLibraryId || quizId;

    if (!learningLibraryId) {
      this.showQuizQuestions = false;
      this.showQuizPage = false;
      this.showQuizCompletion = true;
      return;
    }

    this.showQuizQuestions = false;
    this.showQuizPage = false;
    this.showQuizCompletion = true;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onRetakeQuiz(): void {
    this.showQuizCompletion = false;
    this.showQuizQuestions = false;
    this.showQuizPage = true;
    this.trackSubPage('quiz_intro');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onRevisitLesson(): void {
    this.showQuizCompletion = false;
    this.showQuizQuestions = false;
    this.showQuizPage = false;
    this.trackSubPage('lesson');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private trackSubPage(subPage: LearningLibrarySubPage): void {
    this._adobeLaunchService.trackLearningLibrarySubPage(
      subPage,
      this.selectedLessonTitle
    );
  }

  setDifficulty(level: number): void {
    const levelString = level.toString();

    const index = this.selectedDifficulties.indexOf(levelString);
    if (index > -1) {
      this.selectedDifficulties.splice(index, 1);
    } else {
      this.selectedDifficulties.push(levelString);
    }
    this.categorizeLessons();
  }

  isDifficultySelected(level: number): boolean {
    const levelString = level.toString();
    return this.selectedDifficulties.includes(levelString);
  }
}
